import React, { useEffect, useState, useRef } from "react"
import { doc, getDoc, collection, query, where, getDocs, Timestamp } from "firebase/firestore"
import { db } from "../../../firebase/config"
import {
  DollarCircleOutlined,
  PercentageOutlined,
  FilePdfOutlined,
  SearchOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons"
import {
  Row,
  Col,
  Space,
  Select,
  Typography,
  Spin,
  Descriptions,
  Divider,
  Table,
  Button,
  Card,
  Input,
  Tag,
} from "antd"
import PageHeader from "../../../components/Elements/PageHeader"
import MissingDocList from "../../Admin/Sales/SalesReport/MissingDocList"
import moment from "moment-timezone"
import { Document, Page, Text, View, StyleSheet, Font, PDFDownloadLink } from "@react-pdf/renderer"
import Highlighter from "react-highlight-words"
import InvoicePDFPreview from "../Invoices/Helper/InvoicePDFPreview"

const { Text: AntText, Title } = Typography
const { Option } = Select

// Registering a font for a more professional look. You can host your own fonts.
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf",
      fontWeight: "bold",
    },
  ],
})

const pdfStyles = StyleSheet.create({
  page: {
    fontFamily: "Roboto",
    fontSize: 11,
    padding: 30,
    color: "#333",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    borderBottom: "2px solid #4A90E2",
    paddingBottom: 10,
  },
  headerText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#4A90E2",
  },
  companyInfo: {
    textAlign: "right",
  },
  section: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  addressBlock: {
    width: "45%",
  },
  addressTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
    color: "#4A90E2",
  },
  table: {
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#e0e0e0",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#4A90E2",
    color: "#FFF",
    padding: 5,
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    alignItems: "center",
    padding: 5,
  },
  tableColHeader: {
    width: "25%",
    textAlign: "center",
    fontSize: 10,
  },
  tableCol: {
    width: "25%",
    textAlign: "center",
    fontSize: 10,
  },
  totalSection: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
  },
  totalLabel: {
    width: "75%",
    textAlign: "right",
    fontWeight: "bold",
    fontSize: 12,
    paddingRight: 10,
  },
  totalValue: {
    width: "25%",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: 12,
    color: "#4A90E2",
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 9,
    color: "#888",
    borderTop: "1px solid #e0e0e0",
    paddingTop: 10,
  },
})

const InvoicePDF = ({ invoices, userEmail, monthLabel, year }) => (
  <Document>
    <Page size="A4" style={pdfStyles.page}>
      <View style={pdfStyles.header}>
        <Text style={pdfStyles.headerText}>INVOICE SUMMARY</Text>
        <View style={pdfStyles.companyInfo}>
          <Text style={{ fontWeight: "bold" }}>{userEmail}</Text>
          <Text>
            Report for: {monthLabel} {year}
          </Text>
        </View>
      </View>

      <View style={pdfStyles.table}>
        <View style={pdfStyles.tableHeader}>
          <Text style={[pdfStyles.tableColHeader, { width: "20%" }]}>Date</Text>
          <Text style={[pdfStyles.tableColHeader, { width: "15%" }]}>Series</Text>
          <Text style={[pdfStyles.tableColHeader, { width: "15%" }]}>Number</Text>
          <Text style={[pdfStyles.tableColHeader, { width: "30%", textAlign: "left" }]}>
            Client
          </Text>
          <Text style={[pdfStyles.tableColHeader, { width: "20%", textAlign: "right" }]}>
            Total
          </Text>
        </View>
        {invoices.map((inv) => (
          <View style={pdfStyles.tableRow} key={inv.id}>
            <Text style={[pdfStyles.tableCol, { width: "20%" }]}>
              {moment(inv.documentDate.toDate()).format("YYYY-MM-DD")}
            </Text>
            <Text style={[pdfStyles.tableCol, { width: "15%" }]}>{inv.docPrefix}</Text>
            <Text style={[pdfStyles.tableCol, { width: "15%" }]}>{inv.docNumber}</Text>
            <Text style={[pdfStyles.tableCol, { width: "30%", textAlign: "left" }]}>
              {inv.client.client}
            </Text>
            <Text style={[pdfStyles.tableCol, { width: "20%", textAlign: "right" }]}>
              €{inv.totalData.totalSum.toFixed(2)}
            </Text>
          </View>
        ))}
      </View>

      <View style={pdfStyles.totalSection}>
        <Text style={pdfStyles.totalLabel}>Grand Total:</Text>
        <Text style={pdfStyles.totalValue}>
          €{invoices.reduce((sum, inv) => sum + inv.totalData.totalSum, 0).toFixed(2)}
        </Text>
      </View>

      <Text style={pdfStyles.footer}>Craftika CMS</Text>
    </Page>
  </Document>
)

// --- Main Salaries Component ---
export default function Salaries() {
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1))
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())

  const [userData, setUserData] = useState([])
  const [usersLoading, setUsersLoading] = useState(true)

  const [salesReportData, setSalesReportData] = useState(null)
  const [salesReportLoading, setSalesReportLoading] = useState(false)

  const [invoices, setInvoices] = useState([])
  const [invoicesLoading, setInvoicesLoading] = useState(false)

  // State for table filtering and preview
  const [searchText, setSearchText] = useState("")
  const [searchedColumn, setSearchedColumn] = useState("")
  const searchInput = useRef(null)
  const [isPreviewVisible, setIsPreviewVisible] = useState(false)
  const [previewUrl, setPreviewUrl] = useState("")

  // Fetch sales users
  useEffect(() => {
    getDocs(collection(db, "users"))
      .then((snap) => {
        const sales = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => u.group?.includes("sales"))

        console.log(sales)
        setUserData(sales)
      })
      .finally(() => setUsersLoading(false))
  }, [])

  // Fetch sales report
  useEffect(() => {
    const fetchReport = async () => {
      if (!selectedUser) return
      setSalesReportLoading(true)
      const m = String(selectedMonth).padStart(2, "0")
      const id = `${selectedUser}_${selectedYear}_${m}`
      const snap = await getDoc(doc(db, "salesReports", id))
      setSalesReportData(snap.exists() ? snap.data() : null)
      console.log(snap.data())

      setSalesReportLoading(false)
    }
    fetchReport()
  }, [selectedUser, selectedMonth, selectedYear])

  // Fetch invoices for the selected user and period
  useEffect(() => {
    const fetchInvoices = async () => {
      if (!selectedUser) return
      setInvoicesLoading(true)
      const start = moment
        .tz("Europe/Riga")
        .year(selectedYear)
        .month(selectedMonth - 1)
        .startOf("month")
        .toDate()
      const end = moment(start).add(1, "month").toDate()
      const q = query(
        collection(db, "invoices"),
        where("userId", "==", selectedUser),
        where("documentDate", ">=", Timestamp.fromDate(start)),
        where("documentDate", "<", Timestamp.fromDate(end)),
        where("deleted", "==", false) // Added to fetch only active invoices
      )
      const snap = await getDocs(q)
      setInvoices(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      setInvoicesLoading(false)
    }
    fetchInvoices()
  }, [selectedUser, selectedMonth, selectedYear])

  const formatCurrency = (val) =>
    `€${Number(val || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`

  // --- Table Search & Filter Logic ---
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters) => {
    clearFilters()
    setSearchText("")
  }

  const getColumnSearchProps = (dataIndex, nestedKey = null) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${nestedKey || dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}>
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      const target = nestedKey ? record[dataIndex]?.[nestedKey] : record[dataIndex]
      return target ? target.toString().toLowerCase().includes(value.toLowerCase()) : ""
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  })

  const handleCancelPreview = () => {
    setIsPreviewVisible(false)
    setPreviewUrl("")
  }

  // Calculate total for the table header
  const invoiceTotalSum = invoices.reduce((sum, inv) => sum + (inv.totalData?.totalNetoSum || 0), 0)

  // --- Upgraded Invoice Table Columns ---
  const invoiceColumns = [
    {
      title: "Date",
      dataIndex: ["documentDate"],
      key: "date",
      sorter: (a, b) => a.documentDate.seconds - b.documentDate.seconds,
      render: (ts) => moment(ts.toDate()).format("YYYY-MM-DD"),
    },
    {
      title: "Series",
      dataIndex: "docPrefix",
      key: "series",
      filters: [...new Set(invoices.map((inv) => inv.docPrefix))].map((prefix) => ({
        text: prefix,
        value: prefix,
      })),
      onFilter: (value, record) => record.docPrefix.includes(value),
      render: (prefix) => <Tag color="blue">{prefix}</Tag>,
    },
    {
      title: "Invoice ID",
      dataIndex: "docNumber",
      key: "id",
      sorter: (a, b) => a.docNumber - b.docNumber,
      ...getColumnSearchProps("docNumber"),
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      ...getColumnSearchProps("client", "client"),
      render: (client) => client?.client || "N/A",
    },
    {
      title: "Total Amount",
      dataIndex: ["totalData", "totalNetoSum"],
      key: "total",
      align: "right",
      sorter: (a, b) => a.totalData.totalNetoSum - b.totalData.totalNetoSum,
      render: (n) => formatCurrency(n),
    },
    {
      title: "Show",
      dataIndex: "downloadUrl",
      width: "5%",
      align: "center",
      render: (value) => {
        if (value) {
          return <InvoicePDFPreview pdfUrl={value} />
        } else {
          return <EyeInvisibleOutlined style={{ color: "#8c8c8c" }} />
        }
      },
    },
  ]

  return (
    <div style={{ padding: 16 }}>
      <InvoicePDFPreview
        visible={isPreviewVisible}
        onCancel={handleCancelPreview}
        pdfUrl={previewUrl}
      />
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <PageHeader icon={<DollarCircleOutlined />} text="Salaries" />
        </Col>
        {/* Filters */}
        <Col span={24}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Space size="large" wrap>
              <Space>
                <AntText>User:</AntText>
                <Select
                  value={selectedUser}
                  onChange={setSelectedUser}
                  loading={usersLoading}
                  style={{ width: 220 }}
                  placeholder="Select a user">
                  {userData.map((u) => (
                    <Option key={u.id} value={u.id}>
                      {u.email}
                    </Option>
                  ))}
                </Select>
              </Space>
              <Space>
                <AntText>Year:</AntText>
                <Select value={selectedYear} onChange={setSelectedYear} style={{ width: 120 }}>
                  {[2023, 2024, 2025].map((y) => (
                    <Option key={y} value={y}>
                      {y}
                    </Option>
                  ))}
                </Select>
              </Space>
              <Space>
                <AntText>Month:</AntText>
                <Select value={selectedMonth} onChange={setSelectedMonth} style={{ width: 140 }}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <Option key={m} value={String(m)}>
                      {moment()
                        .month(m - 1)
                        .format("MMMM")}
                    </Option>
                  ))}
                </Select>
              </Space>
            </Space>
          </Card>
        </Col>

        {/* Main Content Area */}
        {selectedUser ? (
          <Row gutter={[16, 16]} style={{ width: "100%" }}>
            {/* Left Column: Performance */}
            <Col xs={24} lg={8}>
              <Card bordered={false} style={{ borderRadius: 12, height: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Title level={5}>Sales Performance</Title>
                  <PercentageOutlined style={{ fontSize: 20, color: "#4A90E2" }} />
                </Row>
                <Divider />
                <Descriptions column={1} size="small" bordered={false}>
                  <Descriptions.Item label="Profit (5% of net)">
                    {salesReportLoading ? (
                      <Spin size="small" />
                    ) : (
                      formatCurrency(salesReportData?.profit)
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="30-Day Missing Payment (-5%)"
                    contentStyle={{
                      color: salesReportData?.missingPaymentDeduction > 0 ? "#ff4d4f" : undefined,
                    }}>
                    –{" "}
                    <MissingDocList
                      linkText={formatCurrency(salesReportData?.missingPaymentDeduction)}
                      collectionName="invoices"
                      ids={salesReportData?.missingPaymentInvoiceIds || []}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Client Bankruptcy (-10%)"
                    contentStyle={{
                      color: salesReportData?.bankruptcyRiskDeduction > 0 ? "#ff4d4f" : undefined,
                    }}>
                    –{" "}
                    {salesReportLoading ? (
                      <Spin size="small" />
                    ) : (
                      formatCurrency(salesReportData?.bankruptcyRiskDeduction)
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Illiquid (60 days)">
                    <MissingDocList
                      linkText={formatCurrency(salesReportData?.stuck60Base)}
                      collectionName="orders"
                      ids={salesReportData?.stuck60OrderIds || []}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Illiquid (30 days)">
                    <MissingDocList
                      linkText={formatCurrency(salesReportData?.stuck30Base)}
                      collectionName="orders"
                      ids={salesReportData?.stuck30OrderIds || []}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="Illiquid (15 days)">
                    <MissingDocList
                      linkText={formatCurrency(salesReportData?.stuck30Base)}
                      collectionName="orders"
                      ids={salesReportData?.stuck30OrderIds || []}
                    />
                  </Descriptions.Item>
                </Descriptions>
                <Divider />
                <Title level={4}>Total Earnings This Month</Title>
                <AntText strong style={{ fontSize: "2em", color: "#4A90E2" }}>
                  {salesReportLoading ? (
                    <Spin />
                  ) : (
                    formatCurrency(salesReportData?.calculatedEarnings)
                  )}
                </AntText>
              </Card>
            </Col>

            {/* Right Column: Invoices Table */}
            <Col xs={24} lg={16}>
              <Card
                bordered={false}
                style={{ borderRadius: 12, height: "100%" }}
                title={
                  <Row justify="space-between" align="middle" style={{ width: "100%" }}>
                    <Col>
                      <Space align="center">
                        <Title level={5} style={{ margin: 0 }}>
                          Invoices for{" "}
                          {moment()
                            .month(selectedMonth - 1)
                            .format("MMMM")}
                        </Title>
                        <Tag color="green">Total: {formatCurrency(invoiceTotalSum)}</Tag>
                      </Space>
                    </Col>
                    <Col>
                      <PDFDownloadLink
                        document={
                          <InvoicePDF
                            invoices={invoices}
                            userEmail={userData.find((u) => u.id === selectedUser)?.email}
                            monthLabel={moment()
                              .month(selectedMonth - 1)
                              .format("MMMM")}
                            year={selectedYear}
                          />
                        }
                        fileName={`Invoices_${
                          userData.find((u) => u.id === selectedUser)?.email
                        }_${selectedYear}_${moment()
                          .month(selectedMonth - 1)
                          .format("MMMM")}.pdf`}>
                        {({ loading }) => (
                          <Button
                            icon={<FilePdfOutlined />}
                            size="small"
                            type="primary"
                            loading={loading}>
                            Export PDF
                          </Button>
                        )}
                      </PDFDownloadLink>
                    </Col>
                  </Row>
                }>
                <Table
                  size="small"
                  dataSource={invoices}
                  columns={invoiceColumns}
                  loading={invoicesLoading}
                  rowKey="id"
                  pagination={{ pageSize: 10, showSizeChanger: true, size: "small" }}
                />
              </Card>
            </Col>
          </Row>
        ) : (
          <Col span={24}>
            <Card style={{ borderRadius: 12, textAlign: "center", padding: 48 }}>
              <Title level={4}>Please select a user to view their salary report.</Title>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  )
}
